import React, { useState, useEffect, useMemo } from 'react';
import {
    Button,
    TextField,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MobileDatePicker } from '@mui/x-date-pickers/MobileDatePicker';
import { Formik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import useDutiesWeeksService from '../../api/services/DutiesWeeksService';
import useTermService from '../../api/services/termService';

const DutiesWeekSchema = Yup.object().shape({
    weekName: Yup.string().required('Week name is required'),
    weekStartDate: Yup.date().required('Week start date is required'),
    weekEndDate: Yup.date()
        .required('Week end date is required')
        .min(Yup.ref('weekStartDate'), 'End date must be after start date'),
    termId: Yup.string().required('Term is required'),
});

const CreateDialog = ({ open, onClose, onSuccess, selectedTermId }) => {
    const { createDutiesWeek } = useDutiesWeeksService();
    const { fetchAllActiveTerms, terms } = useTermService();
    const [initialTermId, setInitialTermId] = useState(selectedTermId || '');
    const [formKey, setFormKey] = useState(0);

    useEffect(() => {
        if (open) {
            fetchAllActiveTerms(0, 100, 'ACTIVE');
            // Reset form key when dialog opens to ensure fresh form state
            setFormKey(prev => prev + 1);
        }
    }, [open]);

    useEffect(() => {
        if (selectedTermId && terms?.some(term => term.id === selectedTermId)) {
            setInitialTermId(selectedTermId);
        } else if (terms?.length && !initialTermId) {
            const active = terms.find(t => t.status === 'ACTIVE');
            setInitialTermId(active?.id || terms[0].id);
        }
    }, [selectedTermId, terms]);

    const initialValues = useMemo(() => ({
        weekName: '',
        weekStartDate: dayjs(),
        weekEndDate: dayjs().add(7, 'day'),
        termId: initialTermId || '',
    }), [initialTermId]);

    const handleSubmit = async (values) => {
        console.log('handleSubmit called with values:', values);
        console.log('Raw weekStartDate:', values.weekStartDate);
        console.log('Raw weekEndDate:', values.weekEndDate);
        console.log('weekStartDate type:', typeof values.weekStartDate);
        console.log('weekEndDate type:', typeof values.weekEndDate);
        
        // Ensure dates are valid dayjs objects before formatting
        // dayjs() can handle dayjs objects, Date objects, strings, etc.
        const startDate = dayjs(values.weekStartDate);
        const endDate = dayjs(values.weekEndDate);

        console.log('Parsed startDate:', startDate);
        console.log('Parsed endDate:', endDate);
        console.log('Start date isValid:', startDate.isValid());
        console.log('End date isValid:', endDate.isValid());
        console.log('Start date formatted:', startDate.format('YYYY-MM-DD'));
        console.log('End date formatted:', endDate.format('YYYY-MM-DD'));

        if (!startDate.isValid() || !endDate.isValid()) {
            console.error('Invalid date values:', { 
                startDate, 
                endDate,
                startDateValid: startDate.isValid(),
                endDateValid: endDate.isValid(),
                rawStart: values.weekStartDate,
                rawEnd: values.weekEndDate
            });
            return;
        }

        const formattedStartDate = startDate.format('YYYY-MM-DD') + 'T00:00:00';
        const formattedEndDate = endDate.format('YYYY-MM-DD') + 'T23:59:59';
        
        console.log('Formatted startDate:', formattedStartDate);
        console.log('Formatted endDate:', formattedEndDate);

        const dutiesWeekDto = {
            weekName: values.weekName,
            weekStartDate: formattedStartDate,
            weekEndDate: formattedEndDate,
            termId: values.termId,
        };

        console.log('Final payload before API call:', JSON.stringify(dutiesWeekDto, null, 2));

        await createDutiesWeek(dutiesWeekDto, () => {
            onSuccess?.();
            onClose();
        });
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>CREATE DUTIES WEEK</DialogTitle>

            <DialogContent>
                <Formik
                    key={`create-form-${formKey}`}
                    initialValues={initialValues}
                    validationSchema={DutiesWeekSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue }) => (
                        <form onSubmit={handleSubmit}>
                            <Grid container spacing={2} mt={1}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Week Name"
                                        name="weekName"
                                        variant="filled"
                                        value={values.weekName || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.weekName && !!errors.weekName}
                                        helperText={touched.weekName && errors.weekName}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        select
                                        fullWidth
                                        label="Term"
                                        name="termId"
                                        variant="filled"
                                        value={values.termId || ''}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.termId && !!errors.termId}
                                        helperText={touched.termId && errors.termId}
                                    >
                                        {terms?.map(term => (
                                            <MenuItem key={term.id} value={term.id}>
                                                {term.termName}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            label="Week Start Date"
                                            value={values.weekStartDate}
                                            onChange={(value) => {
                                                console.log('Start date changed:', value);
                                                setFieldValue('weekStartDate', value, true);
                                            }}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    variant: 'filled',
                                                    error: touched.weekStartDate && !!errors.weekStartDate,
                                                    helperText: touched.weekStartDate && errors.weekStartDate,
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <MobileDatePicker
                                            label="Week End Date"
                                            value={values.weekEndDate}
                                            minDate={values.weekStartDate}
                                            onChange={(value) => {
                                                console.log('End date changed:', value);
                                                setFieldValue('weekEndDate', value, true);
                                            }}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    variant: 'filled',
                                                    error: touched.weekEndDate && !!errors.weekEndDate,
                                                    helperText: touched.weekEndDate && errors.weekEndDate,
                                                },
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Grid>
                            </Grid>

                            <DialogActions sx={{ mt: 3 }}>
                                <Button onClick={onClose} color="secondary" variant="contained">
                                    Cancel
                                </Button>
                                <Button type="submit" variant="contained" color="secondary">
                                    Save
                                </Button>
                            </DialogActions>
                        </form>
                    )}
                </Formik>
            </DialogContent>
        </Dialog>
    );
};

export default CreateDialog;
